/**
 * The shipping, returns and brand facts published in Product structured data.
 *
 * Google's "Merchant listings" report treats any Product carrying an Offer with
 * a price as a merchant listing, and warns when `shippingDetails`,
 * `hasMerchantReturnPolicy` or a global identifier (a `gtin`, or failing that a
 * `brand`) is missing. The product detail pages carried all three; the cards on
 * `/shop` carried none, so all nine items on that one page were flagged.
 *
 * These values existed as literals in three files before this module, and had
 * already drifted once — the detail page advertised £3.95 postage while the
 * cart charged £4.70. Putting them in nine more places was not an option, so
 * they live here: this module is the only place the *published* figures are
 * written. It is the same discipline as `app/lib/fees.ts` and
 * `app/data/clinicDates.ts`.
 *
 * Plain, client-safe module (no `"use client"`, no server imports) because the
 * shop cards are client components and the detail pages are not.
 */

export const SCHEMA_BRAND_NAME = "CF Organic Wellness";

/**
 * Postage as the cart actually charges it. `app/shop/cart/page.tsx` and the two
 * supplier product flows each hold their own copy of these numbers; this is the
 * structured-data copy, and it must agree with them. Structured data that
 * contradicts the till is worse than none — Google checks it against checkout.
 */
export const UK_POSTAGE = 4.7;
export const FREE_SHIPPING_THRESHOLD = 40;

export const RETURN_WINDOW_DAYS = 30;

export type JsonLdObject = Record<string, unknown>;

export const schemaBrand = (): JsonLdObject => ({
  "@type": "Brand",
  name: SCHEMA_BRAND_NAME,
});

export const schemaSeller = (): JsonLdObject => ({
  "@type": "Organization",
  name: SCHEMA_BRAND_NAME,
});

export const schemaReturnPolicy = (): JsonLdObject => ({
  "@type": "MerchantReturnPolicy",
  applicableCountry: "GB",
  returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
  merchantReturnDays: RETURN_WINDOW_DAYS,
  returnMethod: "https://schema.org/ReturnByMail",
  returnFees: "https://schema.org/FreeReturn",
});

/**
 * What this order would actually pay to be posted. Free over the threshold, so
 * the £60 bundle and the guided cleanse genuinely ship at £0 while a £10 tea
 * pays £4.70 — pass the price and the figure follows the cart rather than being
 * decided by hand on each page.
 *
 * `null` (price unknown) falls back to the paid rate: over-stating postage is a
 * recoverable disappointment, under-stating it is a listing Google can
 * disqualify for contradicting checkout.
 */
export const postageFor = (price: number | null | undefined): number =>
  typeof price === "number" && Number.isFinite(price) && price >= FREE_SHIPPING_THRESHOLD
    ? 0
    : UK_POSTAGE;

export const schemaShippingDetails = (price: number | null | undefined): JsonLdObject => ({
  "@type": "OfferShippingDetails",
  shippingRate: {
    "@type": "MonetaryAmount",
    value: postageFor(price),
    currency: "GBP",
  },
  shippingDestination: {
    "@type": "DefinedRegion",
    addressCountry: "GB",
  },
  deliveryTime: {
    "@type": "ShippingDeliveryTime",
    handlingTime: {
      "@type": "QuantitativeValue",
      minValue: 0,
      maxValue: 1,
      unitCode: "DAY",
    },
    transitTime: {
      "@type": "QuantitativeValue",
      minValue: 1,
      maxValue: 2,
      unitCode: "DAY",
    },
  },
});

/** The three fields every merchant listing Offer needs, in one spread. */
export const schemaOfferPolicies = (price: number | null | undefined): JsonLdObject => ({
  seller: schemaSeller(),
  hasMerchantReturnPolicy: schemaReturnPolicy(),
  shippingDetails: schemaShippingDetails(price),
});
